import { useState, useMemo, useCallback } from "react";

import {
  useFavouriteItemsQuery,
  useDeleteFavouriteMutation,
  useAddFavouriteMutation,
  useUpdateFavouriteMutation,
} from "../../../favourites/api";
import { isEqual } from "../../../favourites/utils";
import { FavouriteBulkExportItem } from "../../../favourites/types";

import {
  IFavouriteProviderContextValue,
  IFavouriteProviderContextActions,
  IFavouriteProviderContextState,
} from "./FavouriteProvider.types";
import { useBulkExportContext } from "../BulkExportProvider";
import produce from "immer";
import { cloneDeep, sortBy } from "lodash";
import { virtualConfiguration } from "../../components/BulkExportStepper";
import { isFieldItemDTO, isMeasureItemDTO, useDimensionItemsQuery } from "../../api";
import { useLCID } from "../../../locale";

export function useFavouriteState(): IFavouriteProviderContextValue {
  const context = useBulkExportContext();
  const lcid = useLCID();
  const { data: favouriteItems = [], isLoading: isFavouriteItemsLoading } = useFavouriteItemsQuery();
  const { mutate: updateFavouriteMutation } = useUpdateFavouriteMutation();
  const { mutate: addFavouriteMutation } = useAddFavouriteMutation();
  const { mutate: deleteFavouriteMutation } = useDeleteFavouriteMutation();

  const [currentFavouriteItem, setCurrentFavouriteItemState] = useState<FavouriteBulkExportItem | undefined>(undefined);

  const setCurrentFavouriteSettings = context?.actions?.setCurrentFavouriteSettings;

  const dataSourceId = context?.state?.dataSourceId;
  const { dimensionItems: configurationItems } = useDimensionItemsQuery(lcid, dataSourceId, virtualConfiguration.identifier);
;
  const setCurrentFavouriteItem = useCallback(
    (item: FavouriteBulkExportItem | undefined) => {
      if (!isEqual(currentFavouriteItem, item)) {
        setCurrentFavouriteItemState(item);
      }
    },
    [currentFavouriteItem]
  );

  const savedFavouriteItem = useMemo<FavouriteBulkExportItem | undefined>(() => {
    const savedFavouriteItem = favouriteItems.find((item) => item.id === currentFavouriteItem?.id) as FavouriteBulkExportItem;
    const virtualConfiguration = currentFavouriteItem?.mark?.parameters?.dimensions.find((d) => d.id === "virtual.configuration");
    const newSavedItem = produce(savedFavouriteItem, (draft) => {
      if (virtualConfiguration) {
        if (!draft.mark.parameters.dimensions.some((d) => d.id === virtualConfiguration.id)) {
          draft.mark.parameters.dimensions.push(virtualConfiguration);
        }
      }
    });
    return newSavedItem;
  }, [favouriteItems, currentFavouriteItem?.id]);

  const addFavourite = useCallback(() => {
    if (currentFavouriteItem) {
      addFavouriteMutation(currentFavouriteItem);
    }
  }, [addFavouriteMutation, currentFavouriteItem]);

  const deleteFavourite = useCallback(() => {
    if (savedFavouriteItem) {
      deleteFavouriteMutation(savedFavouriteItem);
    }
  }, [deleteFavouriteMutation, savedFavouriteItem]);

  const updateFavourite = useCallback(() => {
    if (savedFavouriteItem && currentFavouriteItem) {
      const configuration = currentFavouriteItem?.mark?.parameters?.dimensions.find(
        (d) => d.id === virtualConfiguration.hierarchyIdentifier
      );
    
      const configurationIds = configurationItems?.map((c) => c.id);
      !configuration?.selection.forEach((c) => {
        if (!configurationIds?.includes(c)) {
          if (c === "value_trade") {
            configurationItems?.push({
              id: "value_trade",
              isMeasure: true,
              name: "Trade Value (US$)",
            });
          } else {
            configurationItems?.push({
              id: c,
              isField: true,
              name: c.toString().split(".")[0],
            });
          }
          
        }
      });

      const selectedMeasures = configurationItems?.filter(isMeasureItemDTO).filter((it) => configuration?.selection.includes(it.id));
      const selectedFields = configurationItems?.filter(isFieldItemDTO).filter((it) => configuration?.selection.includes(it.id));
      
      const finalMeasures = selectedMeasures!.map((measure) => ({
        fieldName: measure.name,
        id: measure.id as string,
        isSelected: true,
      }));
      const finalDimension = currentFavouriteItem?.mark?.parameters?.dimensions
        .filter((d) => d.id !== virtualConfiguration.hierarchyIdentifier)
        .map((dimension) => {
          const dimensionId = dimension.id;
          const fieldNameItem = selectedFields?.find((field) => field.id.toString().includes(dimensionId));
          const fieldName = fieldNameItem?.name ?? "";
          return {
            ...dimension,
            fieldName,
            isSelected: !!fieldName,
          };
        });
     
      configuration?.selection.forEach((el) => {
        if (!finalDimension.find((dim) => `${el}`.includes(dim.id))) {
          finalDimension.push({
            selection: [],
            isSelected: true,
            fieldName: configurationItems?.find((field) => field.id.toString() === el)?.name || "",
            id: `${el}`.replace(".name", ""),
          });
        }
      });

      const result = produce(currentFavouriteItem, (draft) => {
        draft.mark.parameters.dimensions = finalDimension.filter((d) => d.id !== "value" && d.id !== "source");
        draft.mark.parameters.measures = finalMeasures;
      });
      
      updateFavouriteMutation({ oldFavouriteItem: savedFavouriteItem, newFavouriteItem: result });
    }
  }, [updateFavouriteMutation, savedFavouriteItem, currentFavouriteItem, context?.state?.dataSourceId]);

  const cancelFavouriteChanges = useCallback(() => {
    if (savedFavouriteItem && setCurrentFavouriteSettings) {
      setCurrentFavouriteItem(savedFavouriteItem);
      setCurrentFavouriteSettings(savedFavouriteItem);
    }
  }, [savedFavouriteItem, setCurrentFavouriteItem, setCurrentFavouriteSettings]);

  const compareFavourites = () => {
    const savedFavouritesDimensions = cloneDeep(savedFavouriteItem?.mark?.parameters?.dimensions!);
    const savedFavouritesFormat = savedFavouriteItem?.mark?.parameters?.format!;
    const currentFavouritesDimensions = cloneDeep(currentFavouriteItem?.mark?.parameters?.dimensions!);
    const currentFavouritesFormat = currentFavouriteItem?.mark?.parameters?.format!;

    const isFormatChanged = !(savedFavouritesFormat === currentFavouritesFormat);

    if (Array.isArray(savedFavouritesDimensions) && Array.isArray(currentFavouritesDimensions)) {
      const sortedSaved = sortBy(
        savedFavouritesDimensions.map((el) => ({
          id: el?.id,
          selection: [
            ...new Set(el?.selection.filter((s) => s !== "source.name" && s !== "value.name" && s !== "value_trade.name")),
          ].sort(),
          isSelected: true,
        })),
        "id"
      )
        .filter((f) => f.id !== "value")
        .filter((f) => f.selection.length !== 0);
      const sortedCurrent =
        currentFavouritesDimensions &&
        sortBy(
          currentFavouritesDimensions.map((el) => ({
            id: el?.id,
            selection: [
              ...new Set(el?.selection.filter((s) => s !== "source.name" && s !== "value.name" && s !== "value_trade.name")),
            ].sort(),
            isSelected: true,
          })),
          "id"
        )
          .filter((f) => f.id !== "value")
          .filter((f) => f.selection.length !== 0);
    
      return !isEqual(sortedSaved as any, sortedCurrent as any) || isFormatChanged;
    }
    return false;
  };

  const state = useMemo<IFavouriteProviderContextState>(
    () => ({
      currentFavouriteItem,
      savedFavouriteItem,
      isFavouriteLoading: isFavouriteItemsLoading,
      isFavouriteDisabled: !currentFavouriteItem,
      isFavouriteChanged: !!savedFavouriteItem && !!currentFavouriteItem && compareFavourites(),
    }),
    [savedFavouriteItem, isFavouriteItemsLoading, currentFavouriteItem, compareFavourites]
  );

  const actions = useMemo<IFavouriteProviderContextActions>(
    () => ({
      addFavourite,
      deleteFavourite,
      updateFavourite,
      cancelFavouriteChanges,
      setCurrentFavouriteItem,
    }),
    [addFavourite, deleteFavourite, updateFavourite, cancelFavouriteChanges, setCurrentFavouriteItem]
  );

  const value = useMemo(() => ({ state, actions }), [actions, state]);

  return value;
}
